import { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Brokers() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [positions, setPositions] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ord, setOrd] = useState({ broker: "ibkr", symbol: "SPY", side: "BUY", qty: 10, order_type: "MARKET", limit_price: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!user) return;
    try {
      const [s, p, o] = await Promise.all([
        api.get("/brokers/status"), api.get("/brokers/positions"), api.get("/brokers/orders?limit=25"),
      ]);
      setStatus(s.data); setPositions(p.data); setOrders(o.data.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [user]); // eslint-disable-line

  const connect = async (broker) => {
    try {
      await api.post("/brokers/connect", { broker, account_ref: "PAPER" });
      toast.success(`${broker.toUpperCase()} linked (paper mode)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Connect failed"); }
  };
  const disconnect = async (broker) => {
    try {
      await api.delete(`/brokers/disconnect/${broker}`);
      toast.success(`${broker.toUpperCase()} unlinked`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed"); }
  };
  const placeOrder = async () => {
    setBusy(true);
    try {
      const payload = { ...ord, qty: parseFloat(ord.qty) };
      if (ord.order_type === "LIMIT") payload.limit_price = parseFloat(ord.limit_price);
      else delete payload.limit_price;
      const { data } = await api.post("/brokers/orders", payload);
      toast.success(`${data.side} ${data.qty} ${data.symbol} @ $${fmt(data.fill_price)} (paper)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Order failed"); }
    finally { setBusy(false); }
  };

  if (!user) return (
    <div className="p-8 text-center max-w-lg mx-auto" data-testid="brokers-signin-required">
      <div className="font-serif-elite text-3xl mb-2">Broker Integrations</div>
      <div className="text-[color:var(--text-secondary)] text-sm">Sign in to link brokers.</div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6" data-testid="brokers-page">
      <div className="border-b border-[color:var(--border-default)] pb-4">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">◆ BROKER INTEGRATIONS · PAPER + LIVE READY</div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">Brokerage</h1>
        <p className="text-[color:var(--text-secondary)] text-sm mt-2 max-w-2xl">
          Link IBKR or Zerodha. All orders execute in <span className="text-gold">paper mode</span> until API keys are added to <span className="font-mono-data">backend/.env</span>. Positions and orders are tracked in your account.
        </p>
      </div>

      {status && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[color:var(--border-default)]" data-testid="broker-providers">
          {Object.entries(status.providers).map(([k, v]) => {
            const linked = status.linked?.find((l) => l.broker === k);
            return (
              <div key={k} className="p-4 border-r border-b border-[color:var(--border-default)]" data-testid={`broker-${k}`}>
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="font-serif-elite text-xl text-white">{k === "ibkr" ? "Interactive Brokers" : "Zerodha (Kite Connect)"}</div>
                    <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest mt-1">
                      {v.configured ? "◆ CREDENTIALS SET" : "◆ PAPER MODE"} · {linked ? "LINKED" : "NOT LINKED"}
                    </div>
                  </div>
                  {linked ? (
                    <button data-testid={`disconnect-${k}`} onClick={() => disconnect(k)} className="text-[10px] font-mono-data text-bear border border-bear px-2 py-1 hover:bg-bear hover:text-white transition-colors">UNLINK</button>
                  ) : (
                    <button data-testid={`connect-${k}`} onClick={() => connect(k)} className="bg-white text-black px-3 py-1 text-[10px] font-mono-data tracking-widest hover:opacity-90 transition-colors">LINK PAPER</button>
                  )}
                </div>
                <div className="text-[11px] font-body text-[color:var(--text-secondary)] mt-3">
                  {v.configured ? "Backend has valid credentials. Trades will route live once you sign the OAuth flow." :
                    <>Need creds? <a href={v.signup_url} target="_blank" rel="noreferrer" className="text-gold underline">{v.signup_url}</a></>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {positions && (
        <section data-testid="positions-section">
          <div className="flex items-baseline gap-3 mb-2">
            <h2 className="text-[11px] font-mono-data tracking-widest text-white">POSITIONS</h2>
            <span className="text-[10px] font-body text-[color:var(--text-muted)]">
              CASH ${fmt(positions.cash)} · NET LIQ ${fmt(positions.net_liq)}
            </span>
          </div>
          <div className="border border-[color:var(--border-default)]">
            {(positions.positions || []).length === 0 ? (
              <div className="p-4 text-xs text-[color:var(--text-muted)] font-mono-data">No positions.</div>
            ) : (
              <>
                <div className="grid grid-cols-[80px,80px,100px,100px,100px,100px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">
                  <span>SYMBOL</span><span>QTY</span><span>AVG COST</span><span>MARK</span><span>MKT VALUE</span><span>P&amp;L</span>
                </div>
                {positions.positions.map((p) => {
                  const mv = p.qty * p.mark_price;
                  const pnl = (p.mark_price - p.avg_cost) * p.qty;
                  return (
                    <div key={p.symbol} className="grid grid-cols-[80px,80px,100px,100px,100px,100px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-xs">
                      <span className="font-mono-data text-white">{p.symbol}</span>
                      <span className={`font-mono-data ${p.qty >= 0 ? "text-bull" : "text-bear"}`}>{p.qty}</span>
                      <span className="font-mono-data text-white">${fmt(p.avg_cost)}</span>
                      <span className="font-mono-data text-[color:var(--text-secondary)]">${fmt(p.mark_price)}</span>
                      <span className="font-mono-data text-white">${fmt(mv)}</span>
                      <span className={`font-mono-data ${pnl >= 0 ? "text-bull" : "text-bear"}`}>{pnl >= 0 ? "+" : ""}${fmt(pnl)}</span>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </section>
      )}

      <section data-testid="place-order-section">
        <h2 className="text-[11px] font-mono-data tracking-widest text-white mb-2">PLACE ORDER (PAPER)</h2>
        <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
          <Sel label="BROKER" value={ord.broker} onChange={(v) => setOrd({ ...ord, broker: v })} opts={["ibkr", "zerodha"]} tid="ord-broker" />
          <Inp label="SYMBOL" value={ord.symbol} onChange={(v) => setOrd({ ...ord, symbol: v.toUpperCase() })} tid="ord-symbol" />
          <Sel label="SIDE" value={ord.side} onChange={(v) => setOrd({ ...ord, side: v })} opts={["BUY", "SELL"]} tid="ord-side" />
          <Inp label="QTY" value={ord.qty} onChange={(v) => setOrd({ ...ord, qty: v })} type="number" tid="ord-qty" />
          <Sel label="TYPE" value={ord.order_type} onChange={(v) => setOrd({ ...ord, order_type: v })} opts={["MARKET", "LIMIT"]} tid="ord-type" />
          {ord.order_type === "LIMIT" ? (
            <Inp label="LIMIT $" value={ord.limit_price} onChange={(v) => setOrd({ ...ord, limit_price: v })} type="number" tid="ord-limit" />
          ) : <div />}
        </div>
        <button data-testid="place-order-btn" onClick={placeOrder} disabled={busy} className="mt-2 bg-white text-black px-4 py-2 text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-40">{busy ? "PLACING…" : `${ord.side} ${ord.qty} ${ord.symbol}`}</button>
      </section>

      <section data-testid="orders-section">
        <h2 className="text-[11px] font-mono-data tracking-widest text-white mb-2">RECENT ORDERS</h2>
        <div className="border border-[color:var(--border-default)]">
          {orders.length === 0 ? (
            <div className="p-4 text-xs text-[color:var(--text-muted)] font-mono-data">No orders yet.</div>
          ) : (
            orders.map((o) => (
              <div key={o.id} className="grid grid-cols-[110px,60px,80px,60px,80px,90px,120px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-xs" data-testid={`order-${o.id}`}>
                <span className="font-mono-data text-[color:var(--text-muted)]">{(o.created_at || "").slice(5, 16).replace("T", " ")}</span>
                <span className={`font-mono-data ${o.side === "BUY" ? "text-bull" : "text-bear"}`}>{o.side}</span>
                <span className="font-mono-data text-white">{o.symbol}</span>
                <span className="font-mono-data text-white">{o.qty}</span>
                <span className="font-mono-data text-gold">${fmt(o.fill_price)}</span>
                <span className="font-mono-data text-bull">{o.status}</span>
                <span className="font-mono-data text-[color:var(--text-muted)]">{o.broker.toUpperCase()} {o.paper ? "· PAPER" : ""}</span>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function Inp({ label, value, onChange, type = "text", tid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">{label}</span>
      <input data-testid={tid} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-1.5 text-sm font-mono-data text-white focus:outline-none focus:border-[color:var(--border-focus)]" />
    </label>
  );
}

function Sel({ label, value, onChange, opts, tid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">{label}</span>
      <select data-testid={tid} value={value} onChange={(e) => onChange(e.target.value)} className="bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-1.5 text-sm font-mono-data text-white focus:outline-none focus:border-[color:var(--border-focus)]">
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
